import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Page, Progress, NavButtons, BigTextInput, BigTextArea, ConfirmScreen, StepTitle, DisclaimerBlock } from "@/components/wizard";
import { DISCLAIMERS } from "@/data/content";
import { postPrescription } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Prescription() {
  const [idx, setIdx] = useState(0);
  const [vals, setVals] = useState({ meds: ["", "", "", "", ""], dosages: {} });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [hp, setHp] = useState("");
  const [done, setDone] = useState(false);
  const [idem] = useState(() => crypto.randomUUID());

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [idx, done]);
  const location = useLocation();
  const code = location.state?.code || null;

  const set = (k) => (v) => setVals((s) => ({ ...s, [k]: v }));
  const enteredMeds = vals.meds.map((m) => m.trim()).filter(Boolean);

  const steps = useMemo(() => {
    const s = [{ id: "disclaimer", title: DISCLAIMERS.prescription.title }, { id: "meds", title: "Per quali farmaci richiedi la ricetta?" }];
    enteredMeds.forEach((m, i) => s.push({ id: `dosage:${i}`, title: m, sub: "Dosaggio" }));
    s.push({ id: "pharmacy", title: "A quale farmacia dobbiamo inviare la ricetta?" });
    s.push({ id: "reminder", title: "Vuoi ricevere un promemoria?" });
    s.push({ id: "summary", title: "Riepilogo" });
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vals.meds]);

  const step = steps[Math.min(idx, steps.length - 1)];

  const canContinue = () => {
    if (step.id === "disclaimer") return !!vals.disclaimer;
    if (step.id === "meds") return enteredMeds.length >= 1;
    if (step.id.startsWith("dosage:")) {
      const i = step.id.split(":")[1];
      return (vals.dosages[i] || "").trim().length >= 1;
    }
    if (step.id === "pharmacy") {
      const em = (vals.pharmacy_email || "").trim();
      return (
        (vals.pharmacy_name || "").trim().length >= 1 &&
        (vals.pharmacy_city || "").trim().length >= 1 &&
        (!em || EMAIL_RE.test(em))
      );
    }
    if (step.id === "reminder") {
      const em = (vals.reminder_email || "").trim();
      if (!em) return true;
      return EMAIL_RE.test(em) && !!vals.reminder_days;
    }
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await postPrescription({
        idempotency_key: idem,
        code,
        medications: enteredMeds.map((m, i) => ({ name: m, dosage: (vals.dosages[i] || "").trim() })),
        pharmacy_name: vals.pharmacy_name.trim(),
        pharmacy_city: vals.pharmacy_city.trim(),
        pharmacy_email: (vals.pharmacy_email || "").trim() || null,
        notes: vals.notes || "",
        reminder_email: (vals.reminder_email || "").trim() || null,
        reminder_days: (vals.reminder_email || "").trim() ? vals.reminder_days || null : null,
        website: hp,
      });
      setDone(true);
    } catch (e) {
      setError("Invio non riuscito. Controlla la connessione e riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <ConfirmScreen
        title="Richiesta inviata"
        text="La richiesta di ricetta è stata trasmessa allo studio."
        note="L'invio della richiesta non equivale all'emissione della prescrizione."
      />
    );
  }

  return (
    <Page narrow>
      <Progress current={idx + 1} total={steps.length} />
      <div data-testid={`prescription-step-${step.id.replace(":", "-")}`}>
        <StepTitle title={step.title} sub={step.sub} />

        {step.id === "disclaimer" && (
          <DisclaimerBlock
            content={DISCLAIMERS.prescription}
            checked={!!vals.disclaimer}
            onToggle={() => set("disclaimer")(!vals.disclaimer)}
          />
        )}

        {step.id === "meds" && (
          <div className="mt-8 flex flex-col gap-3">
            {vals.meds.map((m, i) => (
              <input
                key={i}
                value={m}
                onChange={(e) =>
                  setVals((s) => ({ ...s, meds: s.meds.map((x, j) => (j === i ? e.target.value : x)) }))
                }
                placeholder={`Farmaco ${i + 1}`}
                data-testid={`medication-input-${i + 1}`}
                className="w-full min-h-[52px] px-4 py-3 rounded-xl border border-[#E5E7EB] bg-white text-base text-[#142D43] focus:border-[#142D43] focus:ring-2 focus:ring-[#142D43]/10 outline-none transition-[border-color,box-shadow]"
              />
            ))}
          </div>
        )}

        {step.id.startsWith("dosage:") && (
          <BigTextInput
            value={vals.dosages[step.id.split(":")[1]] || ""}
            onChange={(v) => setVals((s) => ({ ...s, dosages: { ...s.dosages, [step.id.split(":")[1]]: v } }))}
            placeholder="es. 50 mg"
            testId={`dosage-input-${step.id.split(":")[1]}`}
            autoFocus
          />
        )}

        {step.id === "pharmacy" && (
          <div className="mt-8 flex flex-col gap-3">
            <BigTextInput value={vals.pharmacy_name || ""} onChange={set("pharmacy_name")} placeholder="Nome della farmacia" testId="pharmacy-name-input" />
            <BigTextInput value={vals.pharmacy_city || ""} onChange={set("pharmacy_city")} placeholder="Località" testId="pharmacy-city-input" />
            <BigTextInput value={vals.pharmacy_email || ""} onChange={set("pharmacy_email")} placeholder="Email della farmacia (facoltativa)" testId="pharmacy-email-input" type="email" />
            <BigTextArea value={vals.notes || ""} onChange={set("notes")} placeholder="Note (facoltative)" testId="pharmacy-notes-input" />
          </div>
        )}

        {step.id === "reminder" && (
          <div className="mt-8">
            <BigTextInput value={vals.reminder_email || ""} onChange={set("reminder_email")} placeholder="nome@email.com" testId="prescription-reminder-email" type="email" />
            <p className="mt-8 text-sm sm:text-base text-[#374151] leading-relaxed">
              Se vuoi, inserisci la tua email e ti ricorderemo tra
            </p>
            <div className="mt-4 flex gap-3">
              {[
                ["14 giorni", 14],
                ["1 mese", 30],
                ["3 mesi", 90],
              ].map(([label, days]) => (
                <button
                  key={days}
                  type="button"
                  data-testid={`reminder-days-${days}`}
                  onClick={() => set("reminder_days")(days)}
                  className={`flex-1 min-h-[52px] px-2 rounded-xl text-sm sm:text-base font-medium transition-[border-color,box-shadow,transform] duration-150 ${
                    vals.reminder_days === days
                      ? "bg-[#F4F6F8] border-2 border-[#142D43] text-[#142D43] font-semibold shadow-[0_3px_10px_rgba(20,45,67,0.08)] active:translate-y-[1px]"
                      : "bg-white border border-[#E5E7EB] text-[#142D43] hover:border-[rgba(20,45,67,0.25)] hover:bg-[#F4F6F8] shadow-[0_3px_10px_rgba(20,45,67,0.05)] active:translate-y-[1px]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="mt-4 text-sm sm:text-base text-[#374151] leading-relaxed">
              di richiedere una nuova ricetta
            </p>
          </div>
        )}

        {step.id === "summary" && (
          <div className="mt-8 border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB]" data-testid="prescription-summary">
            {code ? (
              <div className="px-5 py-4" data-testid="summary-code">
                <p className="text-xs text-[#374151] uppercase tracking-wide">Codice personale</p>
                <p className="mt-1 text-sm sm:text-base text-[#142D43]">{code}</p>
              </div>
            ) : null}
            <div className="px-5 py-4" data-testid="summary-medications">
              <p className="text-xs text-[#374151] uppercase tracking-wide">Farmaci</p>
              {enteredMeds.map((m, i) => (
                <p key={i} className="mt-1 text-sm sm:text-base text-[#142D43]">
                  {m} — {(vals.dosages[i] || "").trim()}
                </p>
              ))}
            </div>
            <div className="px-5 py-4" data-testid="summary-pharmacy">
              <p className="text-xs text-[#374151] uppercase tracking-wide">Farmacia</p>
              <p className="mt-1 text-sm sm:text-base text-[#142D43]">
                {vals.pharmacy_name}, {vals.pharmacy_city} — {vals.pharmacy_email}
              </p>
              {(vals.notes || "").trim() ? <p className="mt-1 text-sm text-[#374151]">{vals.notes}</p> : null}
            </div>
            {(vals.reminder_email || "").trim() ? (
              <div className="px-5 py-4" data-testid="summary-reminder">
                <p className="text-xs text-[#374151] uppercase tracking-wide">Promemoria</p>
                <p className="mt-1 text-sm sm:text-base text-[#142D43]">
                  {vals.reminder_email} — tra {vals.reminder_days === 14 ? "14 giorni" : vals.reminder_days === 30 ? "1 mese" : "3 mesi"}
                </p>
              </div>
            ) : null}
          </div>
        )}

        {error ? <p className="mt-4 text-sm text-red-700" data-testid="prescription-error">{error}</p> : null}

        <input
          type="text"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute left-[-9999px] opacity-0 h-0 w-0"
        />

        <NavButtons
          onBack={() => setIdx(Math.max(0, idx - 1))}
          onNext={() => (step.id === "summary" ? submit() : setIdx(idx + 1))}
          canNext={canContinue()}
          nextLabel={step.id === "summary" ? "INVIA RICHIESTA" : "CONTINUA"}
          nextTestId={step.id === "summary" ? "prescription-form-submit" : "wizard-next-button"}
          showBack={idx > 0}
          loading={submitting}
        />
      </div>
    </Page>
  );
}
